/**
 * Applies a proposal the traveller has confirmed, through the same permission-checked data
 * methods the rest of the app uses. Validates the payload again before touching anything.
 */
import type { DataSource, EventInput } from '../data/source';
import { payloadSchemas, type ActionKind, type ChatAction, type ItemInput } from '../domain/assistant';
import { wallTimeToUtc } from '../domain/time';
import { currencyDigits, splitEqual } from '../domain/money';
import type { ItineraryEvent } from '../domain/model';

export function toMinor(amount: string, currency: string): number {
  const digits = currencyDigits(currency);
  return Math.round(Number(amount) * 10 ** digits);
}

function point(prefix: 'start' | 'end', date: string | null, time: string | null, tz: string | null): Partial<ItineraryEvent> {
  const col = (k: string) => `${prefix}_${k}`;
  if (!date) return { [col('precision')]: 'unknown', [col('date')]: null, [col('local')]: null, [col('tz')]: null, [col('utc')]: null, [col('zone_status')]: 'not_applicable' } as Partial<ItineraryEvent>;
  if (!time) return { [col('precision')]: 'date', [col('date')]: date, [col('local')]: null, [col('tz')]: tz, [col('utc')]: null, [col('zone_status')]: 'not_applicable' } as Partial<ItineraryEvent>;
  const local = `${date}T${time}`;
  const r = wallTimeToUtc(local, tz);
  const base = { [col('precision')]: 'datetime', [col('date')]: date, [col('local')]: local, [col('tz')]: tz };
  const tail = r.status === 'resolved' ? { utc: r.utc, zone_status: 'resolved' }
    : r.status === 'ambiguous' ? { utc: null, zone_status: 'ambiguous' }
    : r.status === 'nonexistent' ? { utc: null, zone_status: 'nonexistent' }
    : { utc: null, zone_status: 'no_zone' };
  return { ...base, [col('utc')]: tail.utc, [col('zone_status')]: tail.zone_status } as Partial<ItineraryEvent>;
}

const TRANSPORT = new Set(['flight', 'rail', 'coach', 'ferry', 'cruise', 'road', 'rental', 'transfer']);

export function itemToEvent(tripId: string, it: Partial<ItemInput>, opts: { confirmed?: boolean } = {}): EventInput {
  const transport = TRANSPORT.has(it.category ?? '');
  const ev: EventInput = {
    trip_id: tripId, title: it.title ?? 'Untitled', category: (it.category ?? 'other') as ItineraryEvent['category'],
    status: opts.confirmed ? 'confirmed' : (it.status ?? 'planned'),
    origin_name: transport ? it.origin_name ?? null : null, destination_name: transport ? it.destination_name ?? null : null,
    venue: transport ? null : it.venue ?? null, address: transport ? null : it.address ?? null,
    operator: it.operator ?? null, service_number: it.service_number ?? null, notes: it.notes ?? null,
    visibility: 'trip', is_fixed: Boolean(opts.confirmed),
    ...point('start', it.start_date ?? null, it.start_time ?? null, it.start_timezone ?? null),
    ...point('end', it.end_date ?? null, it.end_time ?? null, it.end_timezone ?? it.start_timezone ?? null),
  };
  return ev;
}

/** Only fields present in a partial change are touched. */
function changesToPatch(c: Partial<ItemInput>): Partial<ItineraryEvent> {
  const p: Partial<ItineraryEvent> = {};
  for (const k of ['title', 'category', 'origin_name', 'destination_name', 'venue', 'address', 'operator', 'service_number', 'notes', 'status'] as const) {
    if (c[k] !== undefined) (p as Record<string, unknown>)[k] = c[k];
  }
  if (c.start_date !== undefined || c.start_time !== undefined || c.start_timezone !== undefined) Object.assign(p, point('start', c.start_date ?? null, c.start_time ?? null, c.start_timezone ?? null));
  if (c.end_date !== undefined || c.end_time !== undefined || c.end_timezone !== undefined) Object.assign(p, point('end', c.end_date ?? null, c.end_time ?? null, c.end_timezone ?? c.start_timezone ?? null));
  return p;
}

export interface ApplyResult {
  tripId?: string; groupId?: string; count?: number; note?: string; linkCount?: number;
  /** Invitation links to show once for copying. Never saved with the proposal (they work like a key). */
  links?: { label: string; url: string }[];
}

export async function applyAction(data: DataSource, action: ChatAction): Promise<ApplyResult> {
  const kind = action.kind as ActionKind;
  const parsed = payloadSchemas[kind].safeParse(action.payload);
  if (!parsed.success) throw new Error('This proposal is incomplete. Ask Elio to prepare it again.');
  const tripId = action.trip_id ?? undefined;
  const need = () => { if (!tripId) throw new Error('This proposal is not linked to a trip.'); return tripId; };

  switch (kind) {
    case 'create_trip': {
      const p = parsed.data as typeof payloadSchemas.create_trip._output;
      const id = await data.createTrip({
        title: p.title, date_mode: p.date_mode, start_date: p.date_mode === 'unknown' ? null : p.start_date, end_date: p.date_mode === 'unknown' ? null : p.end_date,
        date_note: p.date_note, origin_city: p.origin_city, origin_timezone: p.origin_timezone, reporting_currency: p.currency,
        has_existing_bookings: false, group_id: null, stops: p.stops,
      });
      return { tripId: id };
    }
    case 'update_trip': {
      const p = parsed.data as typeof payloadSchemas.update_trip._output;
      const b = await data.getTrip(need());
      const patch = Object.fromEntries(Object.entries(p).filter(([, v]) => v !== null));
      await data.updateTrip(b.trip.id, b.trip.version, patch);
      return { tripId: b.trip.id };
    }
    case 'set_stops': {
      const p = parsed.data as typeof payloadSchemas.set_stops._output;
      await data.saveStops(need(), p.stops);
      return { tripId, count: p.stops.length };
    }
    case 'add_itinerary_items': {
      const p = parsed.data as typeof payloadSchemas.add_itinerary_items._output;
      for (const it of p.items) await data.createEvent(itemToEvent(need(), it));
      return { tripId, count: p.items.length };
    }
    case 'update_itinerary_item': {
      const p = parsed.data as typeof payloadSchemas.update_itinerary_item._output;
      const b = await data.getTrip(need());
      const ev = b.events.find((e) => e.id === p.event_id);
      if (!ev) throw new Error('That itinerary item no longer exists.');
      await data.updateEvent(ev.id, ev.version, changesToPatch(p.changes));
      return { tripId };
    }
    case 'remove_itinerary_item': {
      const p = parsed.data as typeof payloadSchemas.remove_itinerary_item._output;
      await data.deleteEvent(p.event_id);
      return { tripId };
    }
    case 'add_booking': {
      const p = parsed.data as typeof payloadSchemas.add_booking._output;
      const price = p.booking.price && p.booking.currency ? toMinor(p.booking.price, p.booking.currency) : null;
      await data.createManualBooking(need(), itemToEvent(need(), p.item, { confirmed: true }), {
        provider_name: p.booking.provider_name, reference: p.booking.reference, price_minor: price, currency: price === null ? null : p.booking.currency,
        fare_restrictions: p.booking.fare_restrictions, cancellation_terms: p.booking.cancellation_terms,
      });
      if (p.replaces_event_id) {
        const b = await data.getTrip(need());
        const old = b.events.find((e) => e.id === p.replaces_event_id);
        if (old && old.source !== 'booking') await data.deleteEvent(old.id).catch(() => undefined);
      }
      return { tripId };
    }
    case 'add_expense': {
      const p = parsed.data as typeof payloadSchemas.add_expense._output;
      const minor = toMinor(p.amount, p.currency);
      let shares: { user_id: string; share_minor: number }[] | null = null;
      let visibility: 'private' | 'trip' = 'private';
      if (p.split === 'equal_trip_members') {
        const b = await data.getTrip(need());
        const ids = b.members.filter((m) => m.status === 'accepted').map((m) => m.user_id);
        if (ids.length > 1) { shares = [...splitEqual(minor, ids)].map(([user_id, share_minor]) => ({ user_id, share_minor })); visibility = 'trip'; }
      }
      await data.addExpense({ tripId: need(), amountMinor: minor, currency: p.currency, occurredOn: p.occurred_on, idempotencyKey: `elio-${action.id}`,
        description: p.description, category: p.category, visibility, shares });
      return { tripId };
    }
    case 'add_checklist_items': {
      const p = parsed.data as typeof payloadSchemas.add_checklist_items._output;
      for (const it of p.items) await data.addChecklist(need(), it.title, { visibility: 'private', due_on: it.due_on });
      return { tripId, count: p.items.length };
    }
    case 'set_budget': {
      const p = parsed.data as typeof payloadSchemas.set_budget._output;
      await data.saveBudget(need(), { currency: p.currency, limit_minor: p.total_limit ? toMinor(p.total_limit, p.currency) : null });
      for (const e of p.estimates) {
        await data.saveBudgetItem(need(), {
          category: e.category, description: e.description, currency: p.currency, quantity: 1, unit: 'flat',
          low_unit_minor: toMinor(e.low, p.currency), high_unit_minor: toMinor(e.high, p.currency),
          price_kind: e.source_url ? 'sourced_estimate' : 'rough_benchmark', source: e.source_url, assumptions: e.basis, visibility: 'trip',
        });
      }
      return { tripId, count: p.estimates.length };
    }
    case 'invite_to_trip': {
      const p = parsed.data as typeof payloadSchemas.invite_to_trip._output;
      await data.inviteToTrip(need(), p.people.map((x) => x.user_id), p.role);
      return { tripId, count: p.people.length };
    }
    case 'share_trip_with_group': {
      const p = parsed.data as typeof payloadSchemas.share_trip_with_group._output;
      const b = await data.getTrip(need());
      await data.linkTripToGroup(need(), b.trip.version, p.group_id);
      return { tripId, groupId: p.group_id };
    }
    case 'create_group': {
      const p = parsed.data as typeof payloadSchemas.create_group._output;
      const groupId = await data.createGroup(p.name, p.description);
      const inv = await invite(data, groupId, p.invite_emails, 'member');
      return { groupId, ...inv };
    }
    case 'invite_to_group': {
      const p = parsed.data as typeof payloadSchemas.invite_to_group._output;
      return { groupId: p.group_id, ...(await invite(data, p.group_id, p.emails, p.role, true)) };
    }
  }
}

/** Email invitations where email is set up; otherwise links the traveller shares themselves. */
async function invite(data: DataSource, groupId: string, emails: string[], role: 'member' | 'admin', linkIfNone = false): Promise<Pick<ApplyResult, 'count' | 'note' | 'links' | 'linkCount'>> {
  const links: { label: string; url: string }[] = [];
  let sent = 0;
  for (const email of emails) {
    const r = await data.createInvitation(groupId, { email, role, expiresHours: 168, maxUses: 1 });
    if (r.email === 'sent') sent++; else links.push({ label: `For ${email}`, url: r.link });
  }
  if (!emails.length && linkIfNone) {
    const r = await data.createInvitation(groupId, { email: null, role, expiresHours: 168, maxUses: 10 });
    links.push({ label: 'Invitation link', url: r.link });
  }
  const note = !emails.length ? (linkIfNone ? 'Share this link with the people you want to invite. It works for 7 days.' : undefined)
    : sent === emails.length ? `Invitation emailed to ${sent === 1 ? emails[0] : `${sent} people`}.`
    : `Email isn’t set up yet, so send ${links.length === 1 ? 'this link' : 'these links'} yourself (one per person, valid for 7 days).`;
  return { count: emails.length || links.length, note, links: links.length ? links : undefined, linkCount: links.length };
}
